import { Css, RouteTabWithContent, ScrollableContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { ProjectFeature } from "src/generated/graphqlTypes";
import { usePortalDiv } from "src/hooks";
import { useProjectTitle } from "src/hooks/useProjectTitle";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ActivityTab } from "src/routes/projects/dashboard/components/ActivityTab";
import { DashboardHeader } from "src/routes/projects/dashboard/components/DashboardHeader";
import { DashboardTab } from "src/routes/projects/dashboard/components/DashboardTab";
import { FinancialsTab } from "src/routes/projects/dashboard/components/Financials/FinancialsTab";
import { PreConExpectationsTab } from "src/routes/projects/dashboard/components/PreConExpectationsTab";
import { ProjectParams } from "src/routes/routesDef";
import { createProjectDashboardUrl } from "src/RouteUrls";
import { projectDashboardRoute } from "src/routing/domains/projectsPaths";
import { useProjectContext } from "../context/ProjectContext";

export function DashboardPage() {
  useProjectTitle("Dashboard");
  const { projectId } = useRouteParams<ProjectParams>();
  const [portalDiv, portalRef] = usePortalDiv();
  const { features } = useProjectContext();
  const hasProductConfigPlanFeature = features.includes(ProjectFeature.ProductConfigPlan);

  const tabs: RouteTabWithContent[] = useMemo(
    () => [
      {
        name: "Dashboard",
        href: createProjectDashboardUrl(projectId),
        path: projectDashboardRoute.path,
        render: () => <DashboardTab actionsPortalDiv={portalDiv} />,
      },
      {
        name: "Financials",
        href: createProjectDashboardUrl(projectId, "financials"),
        path: `${projectDashboardRoute.path}/financials`,
        render: () => <FinancialsTab />,
      },
      {
        name: "Activity",
        href: createProjectDashboardUrl(projectId, "activity"),
        path: `${projectDashboardRoute.path}/activity`,
        render: () => <ActivityTab />,
      },
      ...(!hasProductConfigPlanFeature
        ? [
            {
              name: "Pre-Con Expectations",
              href: createProjectDashboardUrl(projectId, "pre-con-expectations"),
              path: `${projectDashboardRoute.path}/pre-con-expectations`,
              render: () => <PreConExpectationsTab />,
            },
          ]
        : []),
    ],
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId],
  );

  return (
    // Omit the default bottom padding of 16px, and add 44px of bottom padding per design request to the bottom of the tabs
    <ScrollableContent omitBottomPadding>
      <DashboardHeader tabs={tabs} headerActionsRef={portalRef} />
      <TabContent tabs={tabs} contentXss={Css.pPx(44).$} />
    </ScrollableContent>
  );
}
